import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Row, Col, Form } from "react-bootstrap";

import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { ReactTags } from "react-tag-autocomplete";
import Select from "react-select";

import Modal from "../../modal";
import { longDay, longMonth, dayOfMonth } from "../../../utils/date";
import { isGuid, phone as formatPhone } from "../../../utils/string";
import "./event.modal.css";
import PeopleSelect from "../../select/person-search";
import MatterSelect from "../../select/matter-search";
import { search as peopleSearch } from "../../../actions/people";
import { item as eventItem } from "../../../actions/calendar.event";
import { normalizeCalendarDescription } from "../../../utils/calendarDescription";

const DEFAULT_EVENT_TYPES = [
  { value: "CTAP", label: "Appearance (Court)" },
  { value: "CNPH", label: "Consultation (Phone)" },
  { value: "CNIP", label: "Consultation (Personal)" },
  { value: "OTHR", label: "Other" },
];

const DEFAULT_EVENT_NAME_TEMPLATE = "Person.LastName $| eventType $| asPhone(Person.phone)";

class EventModal extends React.Component {
  _inviteDebounce = null;

  constructor(props) {
    super(props);
    this.state = this.mapPropsToState(props);
  }

  toUuid = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    return value.uuid ?? value.value ?? null;
  };

  normalizeEventTypeOptions = (value = this.props.eventTypes) => {
    let items = value;

    if (typeof value === "string") {
      try {
        items = JSON.parse(value || "[]");
      } catch (_err) {
        items = [];
      }
    }

    const normalized = (Array.isArray(items) ? items : [])
      .map((item) => ({
        value: String(item?.value ?? "").trim(),
        label: String(item?.label ?? item?.value ?? "").trim(),
      }))
      .filter((item) => item.value && item.label);

    return normalized.length ? normalized : DEFAULT_EVENT_TYPES;
  };

  eventTypeOptions = () => this.normalizeEventTypeOptions();

  defaultEventType = () => {
    const options = this.eventTypeOptions();
    return options.find((option) => option.value === "OTHR")?.value ?? options[0]?.value ?? "";
  };

  eventTypeLabel = (value) => {
    const options = this.eventTypeOptions();
    return options.find((option) => option.value === value)?.label ?? value ?? "";
  };

  normalizePerson = (value) => {
    const person = value?.data ?? value?.person ?? value;
    if (!person || typeof person !== "object") return null;
    const uuid = person.uuid ?? person.value ?? null;
    if (!uuid && !person.firstName && !person.lastName && !person.displayName && !person.phone) return null;

    return {
      uuid,
      displayName: person.displayName ?? person.label ?? "",
      firstName: person.firstName ?? "",
      middleName: person.middleName ?? "",
      lastName: person.lastName ?? "",
      generation: person.generation ?? "",
      email: person.email ?? "",
      phone: person.phone ?? "",
    };
  };

  hydrateTitlePerson = async (value) => {
    const uuid = this.toUuid(value);
    if (!isGuid(uuid)) return;
    if (this.state.titlePerson?.uuid === uuid) return;

    try {
      const resp = await this.props.peopleSearch({ search: uuid, rows: 1 });
      const row = (resp?.items ?? resp?.data?.items ?? resp?.data ?? resp ?? [])[0];
      const person = this.normalizePerson(row);
      if (!person) return;

      this.setState(
        (prev) => {
          if (prev.person !== uuid) return null;
          const next = { ...prev, titlePerson: person };
          const title = prev.titleAuto ? this.buildEventTitle(next) : prev.title;

          return {
            titlePerson: person,
            ...(prev.titleAuto ? { title } : {}),
            settings: {
              ...prev.settings,
              savable: this.computeSavable({ ...next, title }),
            },
          };
        },
        this.doPostChange
      );
    } catch (_err) {
      // Ignore title hydration failures; the user can still type a title.
    }
  };

  settingTemplate = () => {
    const configured = String(this.props.eventNameTemplate ?? "").trim();
    return configured || DEFAULT_EVENT_NAME_TEMPLATE;
  };

  resolveTemplatePath = (rawPath, state) => {
    const path = String(rawPath ?? "").trim();
    if (!path) return "";

    if (path === "eventType") return this.eventTypeLabel(state.eventType);

    const personMatch = path.match(/^Person\.([A-Za-z0-9_]+)$/);
    if (personMatch) {
      const field = personMatch[1];
      const key = field.charAt(0).toLowerCase() + field.slice(1);
      return state.titlePerson?.[key] ?? "";
    }

    return path;
  };

  renderTemplatePart = (part, state) => {
    const token = String(part ?? "").trim();
    if (!token) return "";

    const phoneMatch = token.match(/^asPhone\((.+)\)$/);
    if (phoneMatch) {
      const value = this.resolveTemplatePath(phoneMatch[1], state);
      return formatPhone(value) || value || "";
    }

    return this.resolveTemplatePath(token, state);
  };

  buildEventTitle = (state) => {
    return this.settingTemplate()
      .split(/\s*\$\|\s*/g)
      .map((part) => this.renderTemplatePart(part, state))
      .map((value) => String(value ?? "").trim())
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  };

  applyAutoTitle = () => {
    this.setState(
      (prev) => {
        if (!prev.titleAuto) return null;
        const title = this.buildEventTitle(prev);
        return {
          title,
          settings: {
            ...prev.settings,
            savable: this.computeSavable({ ...prev, title }),
          },
        };
      },
      this.doPostChange
    );
  };

  updateWithAutoTitle = (patch, callback = this.doPostChange) => {
    this.setState(
      (prev) => {
        const next = { ...prev, ...patch };
        const autoPatch = next.titleAuto ? { title: this.buildEventTitle(next) } : {};
        const savableState = { ...next, ...autoPatch };

        return {
          ...patch,
          ...autoPatch,
          settings: {
            ...prev.settings,
            savable: this.computeSavable(savableState),
          },
        };
      },
      callback
    );
  };

  currentUserInvite = () => {
    const user = this.props.auth?.user;
    const uuid = this.toUuid(user);
    if (!isGuid(uuid)) return null;

    return {
      uuid,
      displayName: user?.displayName ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      email: user?.email ?? "",
      phone: user?.phone ?? "",
      userUuid: user?.userUuid ?? null,
    };
  };

  withOpeningPersonInvite = (invites = []) => {
    const current = Array.isArray(invites) ? invites.filter(Boolean) : [];
    if (current.length > 0) return current;

    const userInvite = this.currentUserInvite();
    return userInvite ? [userInvite] : current;
  };

  mapPropsToState = (props) => {
    const start = props?.time?.start ?? null;
    const title = props.title ?? "";
    const eventType = props.eventType ?? props.db?.eventType ?? this.defaultEventType();
    const titlePerson = this.normalizePerson(props.person ?? props.db?.person ?? props.Person);
    const titleAuto = !isGuid(props.uuid) && !isGuid(props.sourceUuid) && !String(title).trim();

    const next = {
      uuid: props.uuid,
      sourceUuid: props.sourceUuid,
      style: props.style,
      eventType,

      calendarUuid: props.calendarUuid ?? null,

      time: {
        start,
        end: props?.time?.end ?? null,
        allDay: !!(props?.time?.allDay ?? props?.allDay),
      },

      invites: this.withOpeningPersonInvite(props.invites ?? []),
      title,
      titleAuto,
      titlePerson,
      description: normalizeCalendarDescription(props.description),
      location: props.location ?? "",
      recurrence: props.recurrence ?? "",
      createdBy: this.toUuid(props.createdBy),
      person: this.toUuid(props.person ?? props.personUuid),
      matter: this.toUuid(props.matter ?? props.caseUuid),
      options: this.recurrenceOptions(start),
      changed: false,
      customRRule: props.rrule ?? "",
    };

    if (next.titleAuto) next.title = this.buildEventTitle(next);

    return {
      settings: {
        savable: this.computeSavable(next),
        invites: { suggestions: [], query: "" },
        calendars: { items: this.toCalendarOptions(props.calendars), loading: false },
      },
      ...next,
    };
  };

  componentDidMount() {
    if (this.props.show) {
      this.hydrateTitlePerson(this.state.person);
      this.applyAutoTitle();
    }
  }

  componentDidUpdate(prevProps) {
    const opened = !prevProps.show && this.props.show;
    const uuidChanged = prevProps.uuid !== this.props.uuid;
    const sourceUuidChanged = prevProps.sourceUuid !== this.props.sourceUuid;
    const calendarsChanged = prevProps.calendars !== this.props.calendars;
    const eventSettingsChanged =
      prevProps.eventTypes !== this.props.eventTypes ||
      prevProps.eventNameTemplate !== this.props.eventNameTemplate;

    if (opened || uuidChanged || sourceUuidChanged) {
      this.setState(this.mapPropsToState(this.props), async () => {
        await this.loadData();
        await this.hydrateTitlePerson(this.state.person);
        this.applyAutoTitle();
      });
      return;
    }

    if (eventSettingsChanged) {
      const options = this.eventTypeOptions();
      const eventType = options.some((option) => option.value === this.state.eventType)
        ? this.state.eventType
        : this.defaultEventType();

      this.updateWithAutoTitle({
        eventType,
      });
    }

    if (calendarsChanged) {
      this.setState((prev) => {
        const items = this.toCalendarOptions(this.props.calendars);
        const allowedUuids = new Set(items.map((x) => x.value));
        const keepSelected = isGuid(prev.calendarUuid) && allowedUuids.has(prev.calendarUuid);
        const calendarUuid = keepSelected ? prev.calendarUuid : (items[0]?.value ?? null);

        return {
          calendarUuid,
          settings: {
            ...prev.settings,
            calendars: { items, loading: false },
            savable: this.computeSavable({
              ...prev,
              calendarUuid,
            }),
          },
        };
      });
    }

    if (prevProps.rrule !== this.props.rrule) {
      this.setState((prev) => {
        const { recurrence, customRRule } = this.interpretRecurrenceFromRRule(this.props.rrule);

        return {
          recurrence,
          customRRule,
          options: this.recurrenceOptions(prev.time?.start),
          settings: {
            ...prev.settings,
            savable: this.computeSavable({
              ...prev,
              recurrence,
            }),
          },
        };
      }, this.doPostChange);
    }
  }

  componentWillUnmount() {
    if (this._inviteDebounce) clearTimeout(this._inviteDebounce);
  }

  parseRRule = (rruleRaw) => {
    if (!rruleRaw) return { freq: null, params: {} };

    const raw = String(rruleRaw).trim();
    const body = raw.toUpperCase().startsWith("RRULE:") ? raw.slice(6) : raw;

    const parts = body.split(";").map((s) => s.trim()).filter(Boolean);
    const params = {};
    for (const part of parts) {
      const [k, v] = part.split("=");
      if (k && v) params[k.toUpperCase()] = v;
    }
    return { freq: params.FREQ || null, params };
  };

  interpretRecurrenceFromRRule = (rruleRaw) => {
    const raw = (rruleRaw ?? "").toString().trim();
    const { freq, params } = this.parseRRule(raw);
    if (!freq) return { recurrence: "", customRRule: "" };

    const byday = (params.BYDAY || "").split(",").map((s) => s.trim()).filter(Boolean);
    const bysetpos = params.BYSETPOS ? parseInt(params.BYSETPOS, 10) : null;
    const bymonth = params.BYMONTH ? parseInt(params.BYMONTH, 10) : null;
    const bymonthday = params.BYMONTHDAY ? parseInt(params.BYMONTHDAY, 10) : null;

    if (freq === "DAILY") return { recurrence: "Daily", customRRule: "" };

    if (freq === "WEEKLY") {
      const weekdaySet = ["MO", "TU", "WE", "TH", "FR"];
      const isEveryWeekday = byday.length === 5 && weekdaySet.every((d) => byday.includes(d));
      if (isEveryWeekday) return { recurrence: "EveryWeekday", customRRule: "" };
      if (byday.length === 1) return { recurrence: "Weekly", customRRule: "" };
      return { recurrence: "Custom", customRRule: raw };
    }

    if (freq === "MONTHLY") {
      if (byday.length === 1 && Number.isInteger(bysetpos) && bysetpos >= 1 && bysetpos <= 5) {
        return { recurrence: "Monthly", customRRule: "" };
      }
      return { recurrence: "Custom", customRRule: raw };
    }

    if (freq === "YEARLY") {
      if (Number.isInteger(bymonth) && Number.isInteger(bymonthday)) {
        return { recurrence: "Annually", customRRule: "" };
      }
      return { recurrence: "Custom", customRRule: raw };
    }

    return { recurrence: "Custom", customRRule: raw };
  };

  loadData = async () => {
    const lookupUuid = isGuid(this.state.sourceUuid) ? this.state.sourceUuid : this.state.uuid;
    if (!isGuid(lookupUuid)) return;

    let item = await this.props.eventItem({ uuid: lookupUuid });
    if (!item && lookupUuid !== this.state.uuid && isGuid(this.state.uuid)) {
      item = await this.props.eventItem({ uuid: this.state.uuid });
    }
    if (!item) return;

    const start = item.startsAt ? new Date(item.startsAt) : null;
    const end = item.endsAt ? new Date(item.endsAt) : null;

    const safeStart = this.isValidDate(start) ? start : null;
    const safeEnd = this.isValidDate(end) ? end : null;

    const invites = this.withOpeningPersonInvite(item.attendees ?? []);

    await new Promise((resolve) => this.setState((prev) => {
      const allowed = prev.settings?.calendars?.items ?? [];
      const allowedUuids = new Set(allowed.map((x) => x.value));
      const dbCalendarUuid = item.db?.calendarUuid ?? null;
      const calendarUuid = allowedUuids.has(dbCalendarUuid)
        ? dbCalendarUuid
        : (prev.calendarUuid ?? allowed[0]?.value ?? null);

      const merged = {
        ...prev,
        calendarUuid,
        uuid: item.db?.uuid ?? item.uuid ?? prev.uuid,
        sourceUuid: item.db?.sourceUuid ?? item.sourceUuid ?? item.uuid ?? prev.sourceUuid,
        style: item.style,
        eventType: item.db?.eventType ?? item.eventType ?? prev.eventType ?? this.defaultEventType(),
        time: {
          start: safeStart,
          end: safeEnd,
          allDay: !!(item.allDay ?? false),
        },
        title: item.title ?? "",
        description: normalizeCalendarDescription(item.description),
        location: item.location ?? "",
        recurrence: item.recurrence ?? "",
        customRRule: item.rrule ?? "",
        createdBy: item.db?.createdByUuid ?? null,
        person: item.db?.personUuid ?? null,
        titlePerson: this.normalizePerson(item.db?.person ?? item.Person ?? item.person) ?? prev.titlePerson,
        titleAuto: false,
        matter: item.db?.caseUuid ?? null,
        invites,
        options: this.recurrenceOptions(safeStart),
        changed: false,
      };

      return {
        ...merged,
        settings: {
          ...prev.settings,
          savable: this.computeSavable(merged),
        },
      };
    }, resolve));
  };

  isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

  computeSavable = (s) => {
    const calendarOk = isGuid(s?.calendarUuid);
    const titleOk = (s?.title ?? "").trim().length > 1;

    const start = s?.time?.start;
    const end = s?.time?.end;
    const timeOk = this.isValidDate(start) && this.isValidDate(end) && end >= start;

    return calendarOk && titleOk && timeOk;
  };

  _formatInviteLabel = (p) => {
    const name = p?.displayName || [p?.firstName, p?.lastName].filter(Boolean).join(" ").trim();
    const email = p?.email ? ` (${p.email})` : "";
    const phone = p?.phone && String(p.phone).trim() ? ` (${String(p.phone).trim()})` : "";
    return `${name}${email}${phone}`.trim();
  };

  _toSuggestion = (p) => ({
    value: p.uuid,
    label: this._formatInviteLabel(p),
    person: p,
  });

  onInviteInput = (value) => {
    const query = (value ?? "").trim();

    this.setState((prev) => ({
      settings: {
        ...prev.settings,
        invites: {
          ...prev.settings.invites,
          query,
        },
      },
    }));

    if (this._inviteDebounce) clearTimeout(this._inviteDebounce);

    if (query.length < 1) {
      this.setState((prev) => ({
        settings: {
          ...prev.settings,
          invites: {
            ...prev.settings.invites,
            suggestions: [],
          },
        },
      }));
      return;
    }

    this._inviteDebounce = setTimeout(async () => {
      try {
        const resp = await this.props.peopleSearch({ search: query });

        const items =
          resp?.items ??
          resp?.data?.items ??
          resp?.payload?.items ??
          resp?.data ??
          resp ??
          [];

        const suggestions = (Array.isArray(items) ? items : []).map(this._toSuggestion);

        const invited = new Set((this.state.invites ?? []).map((p) => p.uuid));
        const filtered = suggestions.filter((s) => !invited.has(s.value));

        this.setState((prev) => ({
          settings: {
            ...prev.settings,
            invites: {
              ...prev.settings.invites,
              suggestions: filtered,
            },
          },
        }));
      } catch (err) {
        console.log("people search failed", err);
        this.setState((prev) => ({
          settings: {
            ...prev.settings,
            invites: {
              ...prev.settings.invites,
              suggestions: [],
            },
          },
        }));
      }
    }, 250);
  };

  onInviteAdd = (tag) => {
    this.setState(
      (prev) => {
        const existing = new Set((prev.invites ?? []).map((p) => p.uuid));

        const match =
          (prev.settings?.invites?.suggestions ?? []).find((s) => s.value === tag.value) ?? null;

        const personToAdd = match?.person;
        if (!personToAdd) return null;
        if (existing.has(personToAdd.uuid)) return null;

        const nextInvites = [...(prev.invites ?? []), personToAdd];

        return {
          invites: nextInvites,
          settings: {
            ...prev.settings,
            invites: {
              ...prev.settings.invites,
              query: "",
              suggestions: [],
            },
            savable: this.computeSavable({
              ...prev,
              invites: nextInvites,
            }),
          },
        };
      },
      this.doPostChange
    );
  };

  onInviteDelete = (index) => {
    this.setState(
      (prev) => {
        const nextInvites = (prev.invites ?? []).filter((_, i) => i !== index);

        return {
          invites: nextInvites,
          settings: {
            ...prev.settings,
            savable: this.computeSavable({
              ...prev,
              invites: nextInvites,
            }),
          },
        };
      },
      this.doPostChange
    );
  };

  atLocalMidnight = (d, end = false) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;
    return end
      ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
      : new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  };

  weekIndex = (start, labeled = false) => {
    if (!(start instanceof Date) || isNaN(start.getTime())) return null;
    const indexOf = Math.ceil((start.getDate() - 1) / 7);
    const label = ["First", "Second", "Third", "Fourth", "Fifth"][indexOf - 1];
    return labeled ? label : indexOf;
  };

  recurrenceOptions = (start) => {
    const safeStart = start instanceof Date && !isNaN(start.getTime()) ? start : new Date();

    return [
      { value: "", label: "Does not repeat" },
      { value: "Daily", label: "Daily" },
      { value: "Weekly", label: `Weekly on ${longDay(safeStart)}` },
      { value: "Monthly", label: `Monthly on the ${this.weekIndex(safeStart)} ${longDay(safeStart)}` },
      { value: "Annually", label: `Annually on ${longMonth(safeStart)} ${dayOfMonth(safeStart)}` },
      { value: "EveryWeekday", label: "Every weekday (Monday to Friday)" },
    ];
  };

  icsByDay = (d) => {
    const map = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;
    return map[d.getDay()];
  };

  weekIndexNumber = (d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;
    return Math.ceil((d.getDate() - 1) / 7);
  };

  toICSRRule = ({ recurrence, start }) => {
    if (!recurrence) return "";
    if (!(start instanceof Date) || isNaN(start.getTime())) return "";

    const byday = this.icsByDay(start);
    const month = start.getMonth() + 1;
    const monthday = start.getDate();
    const setpos = this.weekIndexNumber(start);

    switch (recurrence) {
      case "Daily":
        return "RRULE:FREQ=DAILY";
      case "Weekly":
        return byday ? `RRULE:FREQ=WEEKLY;BYDAY=${byday}` : "RRULE:FREQ=WEEKLY";
      case "EveryWeekday":
        return "RRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR";
      case "Monthly":
        if (!byday || !setpos) return "RRULE:FREQ=MONTHLY";
        return `RRULE:FREQ=MONTHLY;BYDAY=${byday};BYSETPOS=${setpos}`;
      case "Annually":
        return `RRULE:FREQ=YEARLY;BYMONTH=${month};BYMONTHDAY=${monthday}`;
      case "Custom": {
        const raw = (this.state?.customRRule ?? "").toString().trim();
        if (!raw) return "";
        return raw.toUpperCase().startsWith("RRULE:") ? raw : `RRULE:${raw}`;
      }
      default:
        return "";
    }
  };

  toCalendarOptions = (calendars) => {
    return (Array.isArray(calendars) ? calendars : [])
      .filter((cal) => isGuid(cal?.uuid))
      .map((cal) => ({
        value: cal.uuid,
        label: cal.caption || cal.name || "Untitled calendar",
        calendar: cal,
      }));
  };

  doPostChange = () => {
    if (this.props.onChange) this.props.onChange(this.state);
  };

  buildPayload = () => {
    const invites = (this.state.invites ?? []).map((p) => p?.uuid).filter(Boolean);
    const rrule = this.toICSRRule({ recurrence: this.state.recurrence, start: this.state.time?.start });

    return {
      uuid: this.state.uuid,
      sourceUuid: this.state.sourceUuid,
      calendarUuid: this.state.calendarUuid,
      style: this.state.style,
      eventType: this.state.eventType,
      time: this.state.time,
      title: this.state.title,
      description: normalizeCalendarDescription(this.state.description),
      location: this.state.location,
      recurrence: this.state.recurrence,
      createdBy: this.state.createdBy,
      matter: this.state.matter,
      person: this.state.person,
      invites,
      rrule,
    };
  };

  get boardTaskLink() {
    const description = String(this.state?.description || "");
    const match = description.match(/(?:https?:\/\/[^\s]+)?\/dashboard\/people\/[0-9a-f-]{36}\?tab=board&task=[0-9a-f-]{36}/i);
    return match?.[0] || null;
  }

  onOpenBoardTask = () => {
    const link = this.boardTaskLink;
    if (!link) return;
    window.location.assign(link);
  };

  onOk = () => this.props.onOk?.(this.buildPayload());
  onDelete = () => this.props.onDelete?.(this.buildPayload());
  onRemind = () => this.props.onRemind?.(this.buildPayload());
  onCancel = () => this.props.onCancel?.();

  onChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    if (name === "person") {
      const titlePerson = this.normalizePerson(e.target.item);
      this.updateWithAutoTitle({ person: value, titlePerson }, () => {
        this.doPostChange();
        if (!titlePerson) this.hydrateTitlePerson(value);
      });
      return;
    }

    this.updateWithAutoTitle({ [name]: value });
  };

  onRequiredChange = (e) => {
    const patch = {
      [e.target.name]: e.target.value,
      ...(e.target.name === "title" ? { titleAuto: false } : {}),
    };

    this.setState(
      (prev) => ({
        ...patch,
        settings: {
          ...prev.settings,
          savable: this.computeSavable({
            ...prev,
            ...patch,
          }),
        },
      }),
      this.doPostChange
    );
  };

  onAllDayChange = (e) => {
    const checked = e.target.checked;

    this.setState(
      (prev) => {
        const start = prev.time?.start;
        const end = prev.time?.end;

        if (checked) {
          const nextTime = {
            start: this.atLocalMidnight(start),
            end: this.atLocalMidnight(end ?? start, true),
            allDay: true,
          };

          return {
            time: nextTime,
            settings: {
              ...prev.settings,
              savable: this.computeSavable({
                ...prev,
                time: nextTime,
              }),
            },
          };
        }

        const nextTime = { ...prev.time, allDay: false };

        return {
          time: nextTime,
          settings: {
            ...prev.settings,
            savable: this.computeSavable({
              ...prev,
              time: nextTime,
            }),
          },
        };
      },
      this.doPostChange
    );
  };

  onStartTimeChange = (date) => {
    this.setState(
      (prev) => {
        const nextStartRaw = date;
        const nextStart = prev.time?.allDay ? this.atLocalMidnight(nextStartRaw) : nextStartRaw;

        let nextEnd = prev.time?.end;
        if (!(nextEnd instanceof Date) || isNaN(nextEnd.getTime()) || (nextStart && nextEnd < nextStart)) {
          nextEnd = prev.time?.allDay ? this.atLocalMidnight(nextStartRaw, true) : nextStartRaw;
        } else if (prev.time?.allDay) {
          nextEnd = this.atLocalMidnight(nextEnd, true);
        }

        const nextTime = { ...prev.time, start: nextStart, end: nextEnd };

        return {
          time: nextTime,
          options: this.recurrenceOptions(nextStartRaw),
          settings: {
            ...prev.settings,
            savable: this.computeSavable({
              ...prev,
              time: nextTime,
            }),
          },
        };
      },
      this.doPostChange
    );
  };

  onEndTimeChange = (date) => {
    this.setState(
      (prev) => {
        const allDay = !!prev.time?.allDay;
        const nextEnd = allDay ? this.atLocalMidnight(date, true) : date;

        const start = prev.time?.start;
        let finalEnd = nextEnd;

        if (start instanceof Date && !isNaN(start.getTime()) && finalEnd < start) {
          finalEnd = allDay ? this.atLocalMidnight(start, true) : start;
        }

        const nextTime = { ...prev.time, end: finalEnd };

        return {
          time: nextTime,
          settings: {
            ...prev.settings,
            savable: this.computeSavable({
              ...prev,
              time: nextTime,
            }),
          },
        };
      },
      this.doPostChange
    );
  };

  onRecurrenceChange = (opt) => {
    this.setState({ recurrence: opt?.value ?? "" }, this.doPostChange);
  };

  onEventTypeChange = (opt) => {
    this.updateWithAutoTitle({ eventType: opt?.value ?? "" });
  };

  render() {
    const eventTypeOptions = this.eventTypeOptions();
    const selectedEventType =
      eventTypeOptions.find((o) => o.value === this.state.eventType) ?? null;
    const selectedRecurrence =
      (this.state.options ?? []).find((o) => o.value === this.state.recurrence) ?? null;

    const isAllDay = !!this.state?.time?.allDay;
    const savable = !!this.state.settings?.savable;
    const boardTaskLink = this.boardTaskLink;

    return (
      <Modal
        show={this.props.show}
        title="Create an Event"
        size={this.props.size || "xl"}
        className="event-modal"
        buttons={[
          { caption: "Open Task", onEnabledClick: this.onOpenBoardTask, variant: "primary", enabled: true, display: Boolean(boardTaskLink) },
          { caption: "Remind", onEnabledClick: this.onRemind, variant: "primary", enabled: true, display: isGuid(this.state.uuid) },
          { caption: "Delete", onEnabledClick: this.onDelete, variant: "danger", enabled: true, display: true },
          { caption: "Save", onEnabledClick: this.onOk, variant: "primary", enabled: savable, display: true },
          { default: true, caption: "Cancel", onEnabledClick: this.onCancel, variant: "secondary", enabled: true, display: true },
        ]}
      >
        <Row className="g-0 mb-2">
          <Col>
            <div className="mb-3 top-label">
              <Form.Control type="text" name="title" value={this.state.title} onChange={this.onRequiredChange} />
              <Form.Label>TITLE</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0 mb-2">
          <Col>
            <div className="mb-3 top-label">
              <Select
                id="eventType"
                name="eventType"
                value={selectedEventType}
                onChange={this.onEventTypeChange}
                classNamePrefix="react-select"
                placeholder="Select a type..."
                options={eventTypeOptions}
              />
              <Form.Label>Type</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0 mb-2">
          <Col>
            <div className="mb-3 top-label">
              <Select
                id="calendarUuid"
                name="calendarUuid"
                isLoading={!!this.state.settings?.calendars?.loading}
                value={
                  (this.state.settings?.calendars?.items ?? []).find(
                    (o) => o.value === this.state.calendarUuid
                  ) ?? null
                }
                onChange={(opt) => {
                  const calendarUuid = opt?.value ?? null;
                  this.setState(
                    (prev) => ({
                      calendarUuid,
                      settings: {
                        ...prev.settings,
                        savable: this.computeSavable({ ...prev, calendarUuid }),
                      },
                    }),
                    this.doPostChange
                  );
                }}
                classNamePrefix="react-select"
                placeholder="Select a calendar..."
                options={this.state.settings?.calendars?.items ?? []}
              />
              <Form.Label>CALENDAR</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0 mb-2">
          <Col>
            <div className="mb-3 top-label">
              <PeopleSelect name="person" value={this.state.person} onChange={this.onChange}></PeopleSelect>
              <Form.Label>ON BEHALF OF</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0 mb-2">
          <Col>
            <div className="mb-3 top-label">
              <MatterSelect name="matter" value={this.state.matter} onChange={this.onChange}></MatterSelect>
              <Form.Label>IN RE</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0 mb-2">
          <Col md="6" className="pe-md-2">
            <div className="mb-3 top-label">
              <DatePicker
                id="startDate"
                className="form-control"
                selected={this.state.time?.start}
                onChange={this.onStartTimeChange}
                timeIntervals={15}
                showTimeSelect={!isAllDay}
                showTimeInput={!isAllDay}
                dateFormat={isAllDay ? "MMMM d, yyyy" : "MMMM d, yyyy h:mm aa"}
              />
              <Form.Label>START</Form.Label>
            </div>
          </Col>

          <Col md="6" className="ps-md-2">
            <div className="mb-3 top-label">
              <DatePicker
                id="endDate"
                className="form-control"
                selected={this.state.time?.end}
                onChange={this.onEndTimeChange}
                timeIntervals={15}
                showTimeSelect={!isAllDay}
                showTimeInput={!isAllDay}
                dateFormat={isAllDay ? "MMMM d, yyyy" : "MMMM d, yyyy h:mm aa"}
              />
              <Form.Label>END</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0 mb-2 align-items-center">
          <Col md="2">
            <div className="mb-0">
              <Form.Check
                type="switch"
                id="allDay"
                label="All day"
                checked={!!this.state?.time?.allDay}
                onChange={this.onAllDayChange}
              />
            </div>
          </Col>

          <Col md="10">
            <div className="mb-3 top-label">
              <Select
                id="recurrencies"
                name="recurrencies"
                value={selectedRecurrence}
                onChange={this.onRecurrenceChange}
                classNamePrefix="react-select"
                placeholder="Does not repeat"
                options={this.state.options}
              />
              <Form.Label>RECURRENCE</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0 mb-2">
          <Col>
            <div className="mb-3 top-label">
              <Form.Control type="text" name="location" value={this.state.location} onChange={this.onChange} />
              <Form.Label>LOCATION</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0">
          <Col lg="12">
            <div className="mb-3 top-label">
              <ReactTags
                selected={(this.state.invites ?? []).map((p) => ({
                  value: p.uuid,
                  label: this._formatInviteLabel(p),
                }))}
                suggestions={this.state.settings?.invites?.suggestions ?? []}
                onInput={this.onInviteInput}
                onAdd={this.onInviteAdd}
                onDelete={this.onInviteDelete}
                allowNew={false}
                placeholderText="Type a name, email, or phone..."
                activateFirstOption
                collapseOnSelect
                delimiterKeys={["Enter", "Tab", ","]}
              />
              <Form.Label>ATTENDEES</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-0">
          <Col>
            <div className="mb-3 top-label">
              <Form.Control
                as="textarea"
                rows={5}
                name="description"
                value={this.state?.description ?? ""}
                onChange={this.onChange}
              />
              <Form.Label>DESCRIPTION</Form.Label>
            </div>
          </Col>
        </Row>
      </Modal>
    );
  }
}

EventModal.propTypes = {
  show: PropTypes.bool,
  size: PropTypes.string,
  calendars: PropTypes.array,
  eventTypes: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  eventNameTemplate: PropTypes.string,

  onOk: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  onRemind: PropTypes.func,
  onChange: PropTypes.func,

  peopleSearch: PropTypes.func.isRequired,
  eventItem: PropTypes.func.isRequired,
};

EventModal.defaultProps = {
  calendars: [],
  eventTypes: DEFAULT_EVENT_TYPES,
  eventNameTemplate: DEFAULT_EVENT_NAME_TEMPLATE,
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps, { peopleSearch, eventItem })(EventModal);
